/**
 * Journey Tracker
 * Lightweight client-side analytics for tracking user journeys
 */

import { getConsent, CONSENT_CHANGE_EVENT } from './consent';

const API_URL = 'https://api.1stprinciples.pro/api/journey/events';
const FLUSH_INTERVAL_MS = 5000; // Flush every 5 seconds
const MAX_QUEUE_SIZE = 10; // Flush when queue reaches this size
const STORAGE_KEY_ANONYMOUS_ID = 'qa_anonymous_id';
const STORAGE_KEY_VISIT_COUNT = 'qa_visit_count';

// Bot detection heuristic (matches index.html)
function isLikelyBot() {
  const ua = navigator.userAgent.toLowerCase();
  const botPatterns = /bot|crawl|spider|slurp|googlebot|bingbot|yandex|baidu|duckduck|facebookexternalhit|linkedinbot|twitterbot|whatsapp|telegram|headless|phantom|selenium|puppeteer/i;
  return botPatterns.test(ua) || navigator.webdriver === true;
}

// Check if running on localhost (development)
function isLocalhost() {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
}

// Generate UUID
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Get or create anonymous ID (persists across sessions)
function getAnonymousId() {
  let id = localStorage.getItem(STORAGE_KEY_ANONYMOUS_ID);
  if (!id) {
    id = generateUUID();
    localStorage.setItem(STORAGE_KEY_ANONYMOUS_ID, id);
  }
  return id;
}

// Get and increment visit count
function getVisitNumber() {
  const count = parseInt(localStorage.getItem(STORAGE_KEY_VISIT_COUNT) || '0', 10) + 1;
  localStorage.setItem(STORAGE_KEY_VISIT_COUNT, count.toString());
  return count;
}

class JourneyTracker {
  constructor() {
    this.isBot = isLikelyBot();
    this.sessionId = generateUUID();
    // anonymousId/visitNumber write to localStorage, so they're assigned in
    // init() only after analytics consent — never at module import time.
    this.anonymousId = null;
    this.visitNumber = null;
    this.consentListenerAttached = false;
    this.userId = null;
    this.eventQueue = [];
    this.flushTimer = null;
    this.isInitialized = false;
    this.landingPage = window.location.pathname;
    this.referrer = document.referrer;

    // Dwell tracking state
    this.dwellTimers = new Map(); // section_name -> { startTime, element }
    this.activeObservers = new Map(); // element -> observer

    // Bind methods
    this.flush = this.flush.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
  }

  /**
   * Initialize the tracker
   */
  init() {
    if (this.isInitialized) return;

    // Skip tracking for bots
    if (this.isBot) {
      console.log('[JourneyTracker] Bot detected, skipping tracking');
      return;
    }

    // Skip tracking on localhost/development
    if (isLocalhost()) {
      console.log('%c[JourneyTracker] Localhost detected - tracking DISABLED', 'color: orange; font-weight: bold');
      return;
    }

    // React to consent changes (accept later -> start; decline later -> stop + clear)
    if (!this.consentListenerAttached) {
      this.consentListenerAttached = true;
      window.addEventListener(CONSENT_CHANGE_EVENT, (e) => {
        if (e.detail === 'accepted') {
          this.init();
        } else if (e.detail === 'declined') {
          this.disable();
        }
      });
    }

    // Gate on analytics consent (see src/utils/consent.js)
    if (getConsent() !== 'accepted') {
      console.log('[JourneyTracker] No analytics consent - tracking disabled');
      return;
    }

    this.anonymousId = getAnonymousId();
    this.visitNumber = getVisitNumber();
    this.isInitialized = true;

    // Start flush timer
    this.flushTimer = setInterval(this.flush, FLUSH_INTERVAL_MS);

    // Flush on page unload
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // Track page load
    this.track('page_load', 'Page Load', {
      path: window.location.pathname,
      referrer: this.referrer,
      visit_number: this.visitNumber
    });

    console.log('[JourneyTracker] Initialized', {
      sessionId: this.sessionId.slice(0, 8) + '...',
      visitNumber: this.visitNumber
    });
  }

  /**
   * Set user ID (call when user logs in)
   */
  setUserId(userId) {
    this.userId = userId;
    console.log('[JourneyTracker] User identified:', userId);
  }

  /**
   * Clear user ID (call when user logs out)
   */
  clearUserId() {
    this.userId = null;
  }

  /**
   * Track an event
   */
  track(eventType, eventName, metadata = null, durationMs = null) {
    // Silently no-op when disabled (bot, localhost, or no analytics consent)
    if (!this.isInitialized) return;

    const event = {
      event_type: eventType,
      event_name: eventName,
      timestamp: new Date().toISOString(),
      duration_ms: durationMs,
      metadata: metadata
    };

    this.eventQueue.push(event);
    console.log('[JourneyTracker] Event queued:', eventType, eventName);

    // Flush if queue is full
    if (this.eventQueue.length >= MAX_QUEUE_SIZE) {
      this.flush();
    }
  }

  /**
   * Track scroll start (call once on first scroll)
   */
  trackScrollStart() {
    this.track('scroll_start', 'First Scroll');
  }

  /**
   * Track section view with dwell time
   * Uses IntersectionObserver to detect when section enters/exits viewport
   */
  observeSection(element, sectionName) {
    if (!element || this.activeObservers.has(element)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Section entered viewport - start timing
          this.dwellTimers.set(sectionName, {
            startTime: Date.now(),
            element: element
          });
        } else {
          // Section left viewport - record dwell time
          const timer = this.dwellTimers.get(sectionName);
          if (timer) {
            const durationMs = Date.now() - timer.startTime;
            // Only record if viewed for at least 1 second
            if (durationMs >= 1000) {
              this.track('section_view', sectionName, null, durationMs);
            }
            this.dwellTimers.delete(sectionName);
          }
        }
      });
    }, {
      threshold: 0.3 // Trigger when 30% visible
    });

    observer.observe(element);
    this.activeObservers.set(element, observer);
  }

  /**
   * Stop observing a section
   */
  unobserveSection(element) {
    const observer = this.activeObservers.get(element);
    if (observer) {
      observer.disconnect();
      this.activeObservers.delete(element);
    }
  }

  /**
   * Track stock expand with dwell time
   * Call startStockExpand when expanded, stopStockExpand when collapsed
   */
  startStockExpand(symbol) {
    if (!symbol) {
      console.warn('[JourneyTracker] startStockExpand called with no symbol');
      return;
    }
    console.log('[JourneyTracker] Stock expand START:', symbol);
    this.dwellTimers.set(`stock_${symbol}`, {
      startTime: Date.now(),
      symbol: symbol
    });
  }

  stopStockExpand(symbol) {
    if (!symbol) {
      console.warn('[JourneyTracker] stopStockExpand called with no symbol');
      return;
    }
    const timer = this.dwellTimers.get(`stock_${symbol}`);
    if (timer) {
      const durationMs = Date.now() - timer.startTime;
      console.log('[JourneyTracker] Stock expand STOP:', symbol, 'duration:', durationMs, 'ms');
      // Only record if expanded for at least 2 seconds
      if (durationMs >= 2000) {
        this.track('stock_expand', symbol, { symbol }, durationMs);
      } else {
        console.log('[JourneyTracker] Stock expand too short, not tracking:', durationMs, 'ms < 2000ms');
      }
      this.dwellTimers.delete(`stock_${symbol}`);
    } else {
      console.warn('[JourneyTracker] stopStockExpand - no timer found for:', symbol);
    }
  }

  /**
   * Track list selection
   */
  trackListSelect(listId, listName, listType = null) {
    this.track('list_select', listName, {
      list_id: listId,
      list_name: listName,
      list_type: listType
    });
  }

  /**
   * Track user action (button clicks, etc.)
   */
  trackAction(actionName, context = null) {
    this.track('action', actionName, context);
  }

  /**
   * Track filter/config change
   */
  trackFilterChange(filterType, value) {
    this.track('filter_change', filterType, {
      filter_type: filterType,
      value: value
    });
  }

  /**
   * Build the session envelope shared by every payload sent to the server
   */
  sessionMeta() {
    return {
      session_id: this.sessionId,
      anonymous_id: this.anonymousId,
      user_id: this.userId,
      visit_number: this.visitNumber,
      user_agent: navigator.userAgent,
      referrer: this.referrer,
      landing_page: this.landingPage
    };
  }

  /**
   * Flush events to server
   */
  async flush() {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    const payload = {
      session: this.sessionMeta(),
      events: events
    };

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true // Important for beforeunload
      });

      if (!response.ok) {
        console.error('[JourneyTracker] Failed to send events:', response.status);
        // Put events back in queue on failure
        this.eventQueue = [...events, ...this.eventQueue];
      }
    } catch (err) {
      console.error('[JourneyTracker] Error sending events:', err);
      // Put events back in queue on error
      this.eventQueue = [...events, ...this.eventQueue];
    }
  }

  /**
   * Handle page unload - flush remaining events
   */
  handleBeforeUnload() {
    // Record any active dwell timers
    this.dwellTimers.forEach((timer, key) => {
      if (key.startsWith('stock_')) {
        const symbol = timer.symbol;
        const durationMs = Date.now() - timer.startTime;
        if (durationMs >= 2000) {
          this.eventQueue.push({
            event_type: 'stock_expand',
            event_name: symbol,
            timestamp: new Date().toISOString(),
            duration_ms: durationMs,
            metadata: { symbol }
          });
        }
      } else {
        const durationMs = Date.now() - timer.startTime;
        if (durationMs >= 1000) {
          this.eventQueue.push({
            event_type: 'section_view',
            event_name: key,
            timestamp: new Date().toISOString(),
            duration_ms: durationMs,
            metadata: null
          });
        }
      }
    });

    // Track session end
    this.eventQueue.push({
      event_type: 'session_end',
      event_name: 'Session End',
      timestamp: new Date().toISOString(),
      duration_ms: null,
      metadata: null
    });

    // Synchronous flush using sendBeacon if available
    if (navigator.sendBeacon) {
      const payload = {
        session: this.sessionMeta(),
        events: this.eventQueue
      };
      navigator.sendBeacon(API_URL, JSON.stringify(payload));
    } else {
      // Fallback to sync fetch
      this.flush();
    }
  }

  /**
   * Consent declined - stop tracking and clear stored analytics identifiers
   */
  disable() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.eventQueue = [];
    this.isInitialized = false;
    this.anonymousId = null;
    try {
      localStorage.removeItem(STORAGE_KEY_ANONYMOUS_ID);
      localStorage.removeItem(STORAGE_KEY_VISIT_COUNT);
    } catch { /* storage unavailable */ }
    console.log('[JourneyTracker] Consent declined - tracking disabled, identifiers cleared');
  }

  /**
   * Cleanup - call when unmounting
   */
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Disconnect all observers
    this.activeObservers.forEach(observer => observer.disconnect());
    this.activeObservers.clear();

    this.flush();
  }
}

// Export singleton instance
const journeyTracker = new JourneyTracker();
export default journeyTracker;
